import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'antd/dist/antd.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import Select from 'react-select';
import { addedPodcast, fetchedPodcasts, startAddingPodcast, startUpdatingPodcast, updatedPodcast } from '../redux/actions';
import { useAppDispatch } from '../redux/hooks';
import { RootState } from '../redux/store';
import { GroupType, PodcastType } from '../types/AppTypes';
import { addPodcast, updatePodcast } from '../utils/api';
import { prosodyRateValues, voices } from '../utils/voices-data';

interface Props {
  podcasts: PodcastType[];
  groups: GroupType[];
  id: string;
}
interface DispatchProps {
  startAddingPodcast: () => void;
  addedPodcast: (
    id: string,
    title: string,
    category: string,
    author: string,
    language: string,
    publisher: string,
    publicationLocation: string | undefined,
    publicationYear: string | undefined,
    description: string,
    type: string,
    parser: string,
    contentType: number,
    pollyLanguages: string,
    image: File,
    groups: string,
    retrieval: boolean,
    message: string,
    error: boolean,
  ) => {
    type: string;
    payload: {
      id: string;
      title: string;
      category: string;
      author: string;
      language: string;
      publisher: string;
      publicationLocation: string | undefined;
      publicationYear: string | undefined;
      description: string;
      type: string;
      parser: string;
      contentType: number;
      pollyLanguages: string;
      image: File;
      groups: string;
      retrieval: boolean;
      message: string;
      error: boolean;
    };
  };
  startUpdatingPodcast: () => void;
  updatedPodcast: (
    id: string,
    title: string,
    category: string,
    author: string,
    language: string,
    publisher: string,
    publicationLocation: string | undefined,
    publicationYear: string | undefined,
    description: string,
    type: string,
    parser: string,
    contentType: number,
    pollyLanguages: string,
    image: File | null,
    groups: string,
    retrieval: boolean,
    message: string,
    error: boolean,
  ) => {
    type: string;
    payload: {
      id: string;
      title: string;
      category: string;
      author: string;
      language: string;
      publisher: string;
      publicationLocation: string | undefined;
      publicationYear: string | undefined;
      description: string;
      type: string;
      parser: string;
      contentType: number;
      pollyLanguages: string;
      image: File | null;
      groups: string;
      retrieval: boolean;
      message: string;
      error: boolean;
    };
  };
}

type ComposedProps = Props & DispatchProps & RouteComponentProps<{ id: string }>;

const PodcastForm = (props: ComposedProps): JSX.Element => {
  const selectRef = useRef<any>([]);
  const podcastDefaults =
    props.match.params.id !== 'add' && props.podcasts
      ? props.podcasts.filter((item: any) => item.id === props.match.params.id)
      : null;
  let voiceSettings;
  if (podcastDefaults && podcastDefaults[0]?.voiceSettings) {
    voiceSettings = JSON.parse(podcastDefaults[0].voiceSettings);
  } else {
    voiceSettings = {
      prosodyRate: 110,
      sampleRate: 24000,
      languages: {
        'en-US': {
          voiceId: 'Matthew',
          engine: 'neural',
          domain: 'empty',
          engines: ['neural', 'standard'],
          domains: [],
          provider: 'AWS',
        },
        'en-GB': {
          voiceId: 'Amy',
          engine: 'neural',
          domain: 'empty',
          engines: ['neural', 'standard'],
          domains: [],
          provider: 'AWS',
        },
        'ja-JP': {
          voiceId: 'Takumi',
          engine: 'standard',
          domain: 'empty',
          engines: ['neural', 'standard'],
          domains: [],
          provider: 'AWS',
        },
        'zh-CN': {
          voiceId: 'Zhiyu',
          engine: 'neural',
          domain: 'empty',
          engines: ['neural', 'standard'],
          domains: [],
          provider: 'AWS',
        },
        'es-ES': {
          voiceId: 'Lucia',
          engine: 'neural',
          domain: 'empty',
          engines: ['neural', 'standard'],
          domains: [],
          provider: 'AWS',
        },
        'es-MX': {
          voiceId: 'Mia',
          engine: 'neural',
          domain: 'empty',
          engines: ['neural', 'standard'],
          domains: [],
          provider: 'AWS',
        },
        'es-US': {
          voiceId: 'Lupe',
          engine: 'neural',
          domain: 'news',
          engines: ['neural', 'standard'],
          domains: [],
          provider: 'AWS',
        },
      },
    };
  }
  if (!voiceSettings.languages['zh-CN']) {
    voiceSettings.languages['zh-CN'] = {
      provider: 'AWS',
      voiceId: 'Zhiyu',
      gender: 'Female',
      engines: ['neural', 'standard'],
      domains: [],
    };
  }
  if (!voiceSettings.languages['es-ES']) {
    voiceSettings.languages['es-ES'] = {
      provider: 'AWS',
      voiceId: 'Lucia',
      gender: 'Female',
      engines: ['neural', 'standard'],
      domains: [],
    };
  }
  if (!voiceSettings.languages['es-MX']) {
    voiceSettings.languages['es-MX'] = {
      provider: 'AWS',
      voiceId: 'Mia',
      gender: 'Female',
      engines: ['neural', 'standard'],
      domains: [],
    };
  }
  if (!voiceSettings.languages['es-US']) {
    voiceSettings.languages['es-US'] = {
      provider: 'AWS',
      voiceId: 'Lupe',
      gender: 'Female',
      engines: ['neural', 'standard'],
      domains: ['news'],
    };
  }
  const contentTypeData = [
    { key: 0, value: 'private' },
    { key: 1, value: 'public pro' },
    { key: 2, value: 'premium pro' },
    { key: 3, value: 'Inflo Classics' },
    { key: 6, value: 'Large Podcast' },
  ];

  const languageData = [
    { key: 'en-GB', value: 'English GB' },
    { key: 'en-US', value: 'English US' },
    { key: 'ja-JP', value: 'Japanese' },
    { key: 'zh-CN', value: 'Chinese' },
    { key: 'es-ES', value: 'Spanish European' },
    { key: 'es-MX', value: 'Spanish Mexican' },
    { key: 'es-US', value: 'Spanish US' },
  ];

  const [hasRssFeedUrl, setHasRssFeedUrl] = useState((podcastDefaults && podcastDefaults[0]?.numPublishedEpisode) || 0);

  const id = props.match.params.id;

  const enGbVoiceInitialValues = {
    voiceId: voiceSettings.languages['en-GB'].voiceId,
    gender: voiceSettings.languages['en-GB'].gender,
    engines: voices['en-GB'].filter((item) => item.voiceId === voiceSettings.languages['en-GB'].voiceId)[0].engines,
    domains: voices['en-GB'].filter((item) => item.voiceId === voiceSettings.languages['en-GB'].voiceId)[0].domains,
    provider: voiceSettings.languages['en-GB'].provider,
  };

  const enUsVoiceInitialValues = {
    voiceId: voiceSettings.languages['en-US'].voiceId,
    gender: voiceSettings.languages['en-US'].gender,
    engines: voices['en-US'].filter((item) => item.voiceId === voiceSettings.languages['en-US'].voiceId)[0].engines,
    domains: voices['en-US'].filter((item) => item.voiceId === voiceSettings.languages['en-US'].voiceId)[0].domains,
    provider: voiceSettings.languages['en-US'].provider,
  };

  const jaJpVoiceInitialValues = {
    voiceId: voiceSettings.languages['ja-JP'].voiceId,
    gender: voiceSettings.languages['ja-JP'].gender,
    engines: voices['ja-JP'].filter((item) => item.voiceId === voiceSettings.languages['ja-JP'].voiceId)[0].engines,
    domains: voices['ja-JP'].filter((item) => item.voiceId === voiceSettings.languages['ja-JP'].voiceId)[0].domains,
    provider: voiceSettings.languages['ja-JP'].provider,
  };

  const zhCnVoiceInitialValues = {
    voiceId: voiceSettings.languages['zh-CN'].voiceId,
    gender: voiceSettings.languages['zh-CN'].gender,
    engines: voices['zh-CN'].filter((item) => item.voiceId === voiceSettings.languages['zh-CN'].voiceId)[0].engines,
    domains: voices['zh-CN'].filter((item) => item.voiceId === voiceSettings.languages['zh-CN'].voiceId)[0].domains,
    provider: voiceSettings.languages['zh-CN'].provider,
  };

  const esEsVoiceInitialValues = {
    voiceId: voiceSettings.languages['es-ES'].voiceId,
    gender: voiceSettings.languages['es-ES'].gender,
    engines: voices['es-ES'].filter((item) => item.voiceId === voiceSettings.languages['es-ES'].voiceId)[0].engines,
    domains: voices['es-ES'].filter((item) => item.voiceId === voiceSettings.languages['es-ES'].voiceId)[0].domains,
    provider: voiceSettings.languages['es-ES'].provider,
  };

  const esMxVoiceInitialValues = {
    voiceId: voiceSettings.languages['es-MX'].voiceId,
    gender: voiceSettings.languages['es-MX'].gender,
    engines: voices['es-MX'].filter((item) => item.voiceId === voiceSettings.languages['es-MX'].voiceId)[0].engines,
    domains: voices['es-MX'].filter((item) => item.voiceId === voiceSettings.languages['es-MX'].voiceId)[0].domains,
    provider: voiceSettings.languages['es-MX'].provider,
  };

  const esUsVoiceInitialValues = {
    voiceId: voiceSettings.languages['es-US'].voiceId,
    gender: voiceSettings.languages['es-US'].gender,
    engines: voices['es-US'].filter((item) => item.voiceId === voiceSettings.languages['es-US'].voiceId)[0].engines,
    domains: voices['es-US'].filter((item) => item.voiceId === voiceSettings.languages['es-US'].voiceId)[0].domains,
    provider: voiceSettings.languages['es-US'].provider,
  };

  const [submitting, setSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState(false);
  const [showImage, setShowImage] = useState<boolean>(podcastDefaults && podcastDefaults[0]?.imageUrl ? true : false);
  const [enGbVoice, setEnGbVoice] = useState(enGbVoiceInitialValues);
  const [enUsVoice, setEnUsVoice] = useState(enUsVoiceInitialValues);
  const [jaJpVoice, setJaJpVoice] = useState(jaJpVoiceInitialValues);
  const [zhCnVoice, setZhCnVoice] = useState(zhCnVoiceInitialValues);
  const [esEsVoice, setEsEsVoice] = useState(esEsVoiceInitialValues);
  const [esMxVoice, setEsMxVoice] = useState(esMxVoiceInitialValues);
  const [esUsVoice, setEsUsVoice] = useState(esUsVoiceInitialValues);

  const defaultFormValues = {
    id: podcastDefaults ? podcastDefaults[0]?.id : null,
    title: podcastDefaults ? podcastDefaults[0]?.title : null,
    category: podcastDefaults ? podcastDefaults[0]?.category : null,
    author: podcastDefaults ? podcastDefaults[0]?.author : null,
    language: podcastDefaults ? podcastDefaults[0]?.language : 'en-US',
    publisher: podcastDefaults ? podcastDefaults[0]?.publisher : null,
    publicationLocation: podcastDefaults ? podcastDefaults[0]?.publicationLocation : null,
    publicationYear: podcastDefaults ? podcastDefaults[0]?.publicationYear : null,
    description: podcastDefaults ? podcastDefaults[0]?.description : null,
    type: podcastDefaults ? podcastDefaults[0]?.type : 'serial',
    parser: podcastDefaults ? podcastDefaults[0]?.parser : 'Aquarius',
    enGbVoiceId: voiceSettings.languages['en-GB'].voiceId,
    enGbEngine: voiceSettings.languages['en-GB'].engine,
    enGbDomain: voiceSettings.languages['en-GB'].domain ? voiceSettings.languages['en-GB'].domain : 'empty',
    enGbProvider: voiceSettings.languages['en-GB'].provider || 'AWS',
    enUsVoiceId: voiceSettings.languages['en-US'].voiceId,
    enUsEngine: voiceSettings.languages['en-US'].engine,
    enUsDomain: voiceSettings.languages['en-US'].domain ? voiceSettings.languages['en-US'].domain : 'empty',
    enUsProvider: voiceSettings.languages['en-US'].provider || 'AWS',
    jaJpVoiceId: voiceSettings.languages['ja-JP'].voiceId,
    jaJpEngine: voiceSettings.languages['ja-JP'].engine,
    jaJpDomain: voiceSettings.languages['ja-JP'].domain ? voiceSettings.languages['ja-JP'].domain : 'empty',
    jaJpProvider: voiceSettings.languages['ja-JP'].provider || 'AWS',
    zhCnVoiceId: voiceSettings.languages['zh-CN'].voiceId,
    zhCnEngine: voiceSettings.languages['zh-CN'].engine,
    zhCnDomain: voiceSettings.languages['zh-CN'].domain ? voiceSettings.languages['zh-CN'].domain : 'empty',
    zhCnProvider: voiceSettings.languages['zh-CN'].provider || 'AWS',
    esEsVoiceId: voiceSettings.languages['es-ES'].voiceId,
    esEsEngine: voiceSettings.languages['es-ES'].engine,
    esEsDomain: voiceSettings.languages['es-ES'].domain ? voiceSettings.languages['es-ES'].domain : 'empty',
    esEsProvider: voiceSettings.languages['es-ES'].provider || 'AWS',
    esMxVoiceId: voiceSettings.languages['es-MX'].voiceId,
    esMxEngine: voiceSettings.languages['es-MX'].engine,
    esMxDomain: voiceSettings.languages['es-MX'].domain ? voiceSettings.languages['es-MX'].domain : 'empty',
    esMxProvider: voiceSettings.languages['es-MX'].provider || 'AWS',
    esUsVoiceId: voiceSettings.languages['es-US'].voiceId,
    esUsEngine: voiceSettings.languages['es-US'].engine,
    esUsDomain: voiceSettings.languages['es-US'].domain ? voiceSettings.languages['es-US'].domain : 'empty',
    esUsProvider: voiceSettings.languages['es-US'].provider || 'AWS',
    prosodyRate: voiceSettings.prosodyRate,
    contentType: podcastDefaults ? podcastDefaults[0]?.contentType : 0,
    image: podcastDefaults ? podcastDefaults[0]?.imageUrl : null,
    retrieval: podcastDefaults ? podcastDefaults[0]?.retrieval : false,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: defaultFormValues,
  });

  const groupsSelect = () => {
    let groupsList: any[] = [];
    (props.groups || []).map((group: { groupId: any }) => {
      if (group.groupId !== 'ALL_PERMISSIONS_GROUP') {
        groupsList.push({ label: group.groupId, value: group.groupId });
      }
    });
    return groupsList;
  };

  const grps = () => {
    let array: any[] = [];
    let g = podcastDefaults ? podcastDefaults[0]?.groups : null;
    (g || []).map((item) => {
      array.push({ label: item, value: item });
    });
    return array;
  };

  const [groupsValues, setGroupsValues] = useState<string[]>(
    podcastDefaults && podcastDefaults[0] && podcastDefaults[0]?.groups
      ? grps()
      : groupsSelect().filter((group) => group.value.toLowerCase() === 'all')[0],
  );

  const onSelectChange = (e) => {
    let array: string[] = [];
    (e || []).map((g) => {
      array.push(g.value);
    });
    setGroupsValues(array);
  };

  const getBase64 = (file, callback) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      callback(reader.result);
    };
    reader.onerror = function (error) {
      console.log('Error: ', error);
      callback();
    };
  };

  /**
   * reset depended voice fields after form submission
   */
  const resetDependedVoiceFields = async () => {
    setEnGbVoice(enGbVoiceInitialValues);
    setEnUsVoice(enUsVoiceInitialValues);
    setJaJpVoice(jaJpVoiceInitialValues);
    setZhCnVoice(zhCnVoiceInitialValues);
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    setShowAlert(false);
    props.startAddingPodcast();

    let image;

    if (data?.image?.length > 0) {
      image = data?.image[0];
    }

    data.image = image;

    const formData = new FormData();
    formData.append('id', data.id);
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('author', data.author);
    formData.append('language', data.language);
    formData.append('publisher', data.publisher);
    formData.append('publicationLocation', data.publicationLocation);
    formData.append('publicationYear', data.publicationYear);
    formData.append('description', data.description);
    formData.append('type', data.type);
    formData.append('parser', data.parser);
    formData.append('retrieval', data.retrieval);

    if (data.enGbDomain === 'empty') {
      data.enGbDomain = '';
    }

    if (data.enUsDomain === 'empty') {
      data.enUsDomain = '';
    }

    if (data.jaJpDomain === 'empty') {
      data.jaJpDomain = '';
    }

    if (data.zhCnDomain === 'empty') {
      data.zhCnDomain = '';
    }

    const pollyLanguages = {
      languages: {
        'en-GB': {
          domain: data.enGbDomain,
          engine: data.enGbEngine,
          voiceId: data.enGbVoiceId,
          provider: enGbVoice.provider,
        },
        'en-US': {
          domain: data.enUsDomain,
          engine: data.enUsEngine,
          voiceId: data.enUsVoiceId,
          provider: enUsVoice.provider,
        },
        'ja-JP': {
          domain: data.jaJpDomain,
          engine: data.jaJpEngine,
          voiceId: data.jaJpVoiceId,
          provider: jaJpVoice.provider,
        },
        'zh-CN': {
          domain: data.zhCnDomain,
          engine: data.zhCnEngine,
          voiceId: data.zhCnVoiceId,
          provider: zhCnVoice.provider,
        },
        'es-ES': {
          domain: data.esEsDomain,
          engine: data.esEsEngine,
          voiceId: data.esEsVoiceId,
          provider: esEsVoice.provider,
        },
        'es-MX': {
          domain: data.esMxDomain,
          engine: data.esMxEngine,
          voiceId: data.esMxVoiceId,
          provider: esMxVoice.provider,
        },
        'es-US': {
          domain: data.esUsDomain,
          engine: data.esUsEngine,
          voiceId: data.esUsVoiceId,
          provider: esUsVoice.provider,
        },
      },
      prosodyRate: data.prosodyRate,
      sampleRate: 24000,
    };

    formData.append('pollyLanguages', JSON.stringify(pollyLanguages));
    formData.append('contentType', data.contentType);

    let array: any[] = [];
    array.push(groupsValues);
    let gv: any[] = [];
    if (groupsValues && groupsValues?.length === undefined) {
      gv.push(array[0].value);
    } else {
      gv = groupsValues;
    }

    if (gv?.length > 0) {
      formData.append('groups', JSON.stringify(gv));
    }
    if (selectRef !== null && selectRef.current !== null) {
      //selectRef.current.clearValue()
      setGroupsValues(groupsSelect()[0]);
    }
    if (gv?.length > 0) {
      getBase64(data.image, (base64File) => {
        formData.append('image', base64File);
        addPodcast(formData)
          .then((response) => {
            props.addedPodcast(
              data.id,
              data.title,
              data.category,
              data.author,
              data.language,
              data.publisher,
              data.publicationLocation,
              data.publicationYear,
              data.description,
              data.type,
              data.parser,
              data.contentType,
              JSON.stringify(pollyLanguages),
              data.image,
              JSON.stringify(gv),
              data.retrieval,
              'Started podcast add',
              false,
            );

            dispatch(fetchedPodcasts([response, ...props.podcasts]));

            resetDependedVoiceFields();
            reset(defaultFormValues);
            setShowAlert(true);
            setSubmitting(false);
            setError(false);
          })
          .catch((error) => {
            props.addedPodcast(
              data.id,
              data.title,
              data.category,
              data.author,
              data.language,
              data.publisher,
              data.publicationLocation,
              data.publicationYear,
              data.description,
              data.type,
              data.parser,
              data.contentType,
              JSON.stringify(pollyLanguages),
              data.image,
              JSON.stringify(gv),
              data.retrieval,
              'Failed to add podcast',
              true,
            );
            setShowAlert(true);
            setSubmitting(false);
            console.log(error);
            setError(true);
          });
      });
    }
  };

  /**
   * send a put request to update edited data
   * @param data the data passed through form fields
   */
  const onUpdate = async (data) => {
    /**
     * TODO::: send updating request
     *
     */
    setSubmitting(true);
    setShowAlert(false);

    let image = null;

    if (!showImage && data?.image?.length > 0) {
      image = data?.image[0];
    }

    data.image = image;

    const formData = new FormData();
    formData.append('id', data.id);
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('author', data.author);
    formData.append('language', data.language);
    formData.append('publisher', data.publisher);
    formData.append('publicationLocation', data.publicationLocation);
    formData.append('publicationYear', data.publicationYear);
    formData.append('description', data.description);
    formData.append('type', data.type);
    formData.append('parser', data.parser);
    formData.append('retrieval', data.retrieval);

    if (data.enGbDomain === 'empty') {
      data.enGbDomain = '';
    }

    if (data.enUsDomain === 'empty') {
      data.enUsDomain = '';
    }

    if (data.jaJpDomain === 'empty') {
      data.jaJpDomain = '';
    }

    if (data.zhCnDomain === 'empty') {
      data.zhCnDomain = '';
    }

    const pollyLanguages = {
      languages: {
        'en-GB': {
          domain: data.enGbDomain,
          engine: data.enGbEngine,
          voiceId: data.enGbVoiceId,
          provider: enGbVoice.provider,
        },
        'en-US': {
          domain: data.enUsDomain,
          engine: data.enUsEngine,
          voiceId: data.enUsVoiceId,
          provider: enUsVoice.provider,
        },
        'ja-JP': {
          domain: data.jaJpDomain,
          engine: data.jaJpEngine,
          voiceId: data.jaJpVoiceId,
          provider: jaJpVoice.provider,
        },
        'zh-CN': {
          domain: data.zhCnDomain,
          engine: data.zhCnEngine,
          voiceId: data.zhCnVoiceId,
          provider: zhCnVoice.provider,
        },
        'es-ES': {
          domain: data.esEsDomain,
          engine: data.esEsEngine,
          voiceId: data.esEsVoiceId,
          provider: esEsVoice.provider,
        },
        'es-MX': {
          domain: data.esMxDomain,
          engine: data.esMxEngine,
          voiceId: data.esMxVoiceId,
          provider: esMxVoice.provider,
        },
        'es-US': {
          domain: data.esUsDomain,
          engine: data.esUsEngine,
          voiceId: data.esUsVoiceId,
          provider: esUsVoice.provider,
        },
      },
      prosodyRate: data.prosodyRate,
      sampleRate: 24000,
    };

    formData.append('pollyLanguages', JSON.stringify(pollyLanguages));
    formData.append('contentType', data.contentType);

    let stop = false;
    let array: any[] = [];
    let gv: any[] = [];
    (groupsValues || []).map((item) => {
      array.push(item);
    });
    !stop &&
      (array || []).map((item) => {
        if (item.value !== undefined) {
          gv.push(item.value);
        } else {
          gv = groupsValues;
          stop = true;
        }
      });
    if (gv.length > 0) {
      formData.append('groups', JSON.stringify(gv));
    }

    if (gv?.length > 0) {
      const updateFunction = () => {
        updatePodcast(data.id, formData)
          .then((response) => {
            props.updatedPodcast(
              data.id,
              data.title,
              data.category,
              data.author,
              data.language,
              data.publisher,
              data.publicationLocation,
              data.publicationYear,
              data.description,
              data.type,
              data.parser,
              data.contentType,
              JSON.stringify(pollyLanguages),
              data.image,
              JSON.stringify(groupsValues),
              data.retrieval,
              'Started podcast update',
              false,
            );

            dispatch(
              fetchedPodcasts(
                props.podcasts.map((item) => {
                  const timestamp = new Date().getTime();

                  if (item.id === data.id) {
                    return { ...item, ...response, imageUrl: `${response.imageUrl}?${timestamp}` };
                  }
                  return item;
                }),
              ),
            );

            setShowAlert(true);
            setSubmitting(false);
            setError(false);
          })
          .catch((error) => {
            props.updatedPodcast(
              data.id,
              data.title,
              data.category,
              data.author,
              data.language,
              data.publisher,
              data.publicationLocation,
              data.publicationYear,
              data.description,
              data.type,
              data.parser,
              data.contentType,
              JSON.stringify(pollyLanguages),
              data.image,
              JSON.stringify(groupsValues),
              data.retrieval,
              'Failed to update podcast',
              true,
            );
            setShowAlert(true);
            setSubmitting(false);
            console.log(error);
            setError(true);
          });
      };

      if (data.image !== null) {
        getBase64(data.image, (base64File) => {
          if (base64File) {
            formData.append('image', base64File);
          }

          updateFunction();
        });
      } else {
        //image is not updated and won't be send

        updateFunction();
      }
    }
  };

  /**
   * handle action on submitting. If user try to add new podcast calls [onSubmit] else [onUpdate] functions
   * @param data the data passed through form fields
   */
  const onSubmitActionHandler = async (data) => {
    if (id === 'add') {
      await onSubmit(data);
    } else {
      await onUpdate(data);
      setShowAlert(true);
    }
  };

  const alertVariant = error ? 'danger' : 'success';

  const alertMessage = error ? 'Failed to add podcast.' : 'Podcast added.';
  const alertUpdateMessage = error ? 'Failed to update podcast.' : 'Podcast updated.';

  const dispatch = useAppDispatch();

  const rssLink = () => {
    if (props.match.params.id !== 'add') {
      const link = process.env.REACT_APP_MP3_SERVER_URL?.replace(/\/file\//, '/');
      const mp3RssUrl = `${link}rss/${props.match.params.id}.rss`;
      return mp3RssUrl;
    }
    return '';
  };

  return (
    <div>
      {showAlert && (
        <Alert variant={alertVariant} dismissible onClose={() => setShowAlert(false)}>
          {id === 'add' ? alertMessage : alertUpdateMessage}
        </Alert>
      )}
      <form onSubmit={handleSubmit(onSubmitActionHandler)} encType="multipart/form-data">
        <div className="mb-4">
          <h2 className="text-4xl font-medium leading-tight mt-0 text-black-600">
            {id === 'add' ? 'Add a podcast' : `Edit ${id} podcast`}
          </h2>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Podcast ID:</label>
          </div>
          <div className="w-40">
            <input
              disabled={submitting || id !== 'add'}
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              type="text"
              {...register('id', { required: true, pattern: /^[a-zA-Z0-9]+$/gi })}
            />
            {errors.id?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
            {errors.id?.type === 'pattern' && (
              <div className="text-red-500 text-left">Enter only alphanumeric characters</div>
            )}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Title:</label>
          </div>
          <div className="w-40">
            <input
              disabled={submitting}
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              type="text"
              {...register('title', { required: true })}
            />
            {errors.title?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Category:</label>
          </div>
          <div>
            <input
              disabled={submitting}
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              type="text"
              {...register('category', { required: true })}
            />
            {errors.category?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Author:</label>
          </div>
          <div>
            <input
              disabled={submitting}
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              type="text"
              {...register('author', { required: true })}
            />
            {errors.author?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Publisher:</label>
          </div>
          <div>
            <input
              disabled={submitting}
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              type="text"
              {...register('publisher', { required: true })}
            />
            {errors.publisher?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Publisher Location:</label>
          </div>
          <div>
            <input
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              type="text"
              {...register('publicationLocation')}
            />
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Publication Year:</label>
          </div>
          <div>
            <input
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              type="text"
              {...register('publicationYear', { pattern: /^\d{4}$|^$/gi })}
            />
            {errors.publicationYear?.type === 'pattern' && (
              <div className="text-red-500 text-left">Enter a valid year: YYYY</div>
            )}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Description:</label>
          </div>
          <div className="w-40">
            <textarea
              rows={5}
              disabled={submitting}
              className="appearance-none border border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white"
              {...register('description', { required: true })}
            />
            {errors.description?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Type:</label>
          </div>
          <div>
            <Form.Control as="select" {...register('type', { required: true })}>
              <option value="episodic">episodic</option>
              <option value="serial">serial</option>
            </Form.Control>
            {errors.type?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Parser:</label>
          </div>
          <div>
            <Form.Control as="select" {...register('parser', { required: true })}>
              <option value="Aquarius">Aquarius</option>
              <option value="Darden">Darden</option>
            </Form.Control>
            {errors.parser?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Image:</label>
          </div>
          <div>
            {!showImage ? (
              <>
                <input type="file" id="img" accept="image/png" multiple {...register('image', { required: true })} />
                {errors.image?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
              </>
            ) : (
              <>
                <div className="d-flex align-items-start" style={{ position: 'relative' }}>
                  <img
                    src={podcastDefaults ? podcastDefaults[0]?.imageUrl : ''}
                    width={60}
                    height={60}
                    alt={
                      podcastDefaults
                        ? podcastDefaults[0]?.imageUrl
                            .substring(podcastDefaults[0]?.imageUrl.lastIndexOf('/') + 1)
                            .split('.')[0]
                        : ''
                    }
                  />
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    size="lg"
                    color="red"
                    style={{ cursor: 'pointer', marginLeft: '2px' }}
                    onClick={() => {
                      setShowImage(false);
                      console.log('clicked');
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Content type:</label>
          </div>
          <div>
            <Form.Control as="select" {...register('contentType', { required: true })}>
              {contentTypeData.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.value}
                </option>
              ))}
            </Form.Control>
            {errors.contentType?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Language:</label>
          </div>
          <div>
            <Form.Control as="select" {...register('language', { required: true })}>
              {languageData.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.value}
                </option>
              ))}
            </Form.Control>
            {errors.language?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>
        {rssLink() !== '' && hasRssFeedUrl > 0 && (
          <div className="flex items-center mb-6">
            <div className="width-fix">
              <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">RSS Feed URL:</label>
            </div>
            <div>
              <p className="text-gray-700 leading-tight mb-0 pl-1">
                <a href={rssLink()} target="_blank" rel="noreferrer">{`${props.match.params.id} RSS URL`}</a>
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Voice en-GB:</label>
          </div>
          <div className="mr-2">
            <Form.Control
              as="select"
              {...register('enGbVoiceId', { required: true })}
              onChange={(e) => {
                const filtered = voices['en-GB'].filter((item) => item.voiceId === e.target.value);
                setEnGbVoice((prevState) => {
                  return {
                    ...prevState,
                    voiceId: e.target.value,
                    engines: filtered[0].engines,
                    domains: filtered[0].domains,
                    gender: filtered[0].gender,
                    provider: filtered[0].provider,
                  };
                });
              }}
            >
              {voices['en-GB']?.map((item) => (
                <option key={item.voiceId} value={item.voiceId}>
                  {item.provider}: {item.voiceId} - {item.gender}
                </option>
              ))}
            </Form.Control>
            {errors.enGbVoiceId?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div className="mr-2">
            <Form.Control as="select" {...register('enGbEngine', { required: true })}>
              {enGbVoice?.engines?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.enGbEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'block' }}>
            <Form.Control as="select" {...register('enGbDomain', { required: true })}>
              {enGbVoice?.domains?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="empty">empty</option>
            </Form.Control>
            {errors.enGbDomain?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'none' }}>
            <Form.Control as="select" {...register('enGbProvider', { required: true })}>
              <option key={enGbVoice.provider} value={enGbVoice.provider}>
                {enGbVoice.provider}
              </option>
            </Form.Control>
            {errors.enGbProvider?.type === 'required' && (
              <div className="text-red-500 text-left">This field is required</div>
            )}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Voice en-US:</label>
          </div>
          <div className="mr-2">
            <Form.Control
              as="select"
              {...register('enUsVoiceId', { required: true })}
              onChange={(e) => {
                const filtered = voices['en-US'].filter((item) => item.voiceId === e.target.value);
                setEnUsVoice((prevState) => {
                  return {
                    ...prevState,
                    voiceId: e.target.value,
                    engines: filtered[0].engines,
                    domains: filtered[0].domains,
                    gender: filtered[0].gender,
                    provider: filtered[0].provider,
                  };
                });
              }}
            >
              {voices['en-US']?.map((item) => (
                <option key={item.voiceId} value={item.voiceId}>
                  {item.provider}: {item.voiceId} - {item.gender}
                </option>
              ))}
            </Form.Control>
            {errors.enUsVoiceId?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div className="mr-2">
            <Form.Control as="select" {...register('enUsEngine', { required: true })}>
              {enUsVoice?.engines?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.enUsEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'block' }}>
            <Form.Control as="select" {...register('enUsDomain', { required: true })}>
              {enUsVoice?.domains?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="empty">empty</option>
            </Form.Control>
            {errors.enUsDomain?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'none' }}>
            <Form.Control as="select" {...register('enUsProvider', { required: true })}>
              <option key={enUsVoice.provider} value={enUsVoice.provider}>
                {enUsVoice.provider}
              </option>
            </Form.Control>
            {errors.enUsProvider?.type === 'required' && (
              <div className="text-red-500 text-left">This field is required</div>
            )}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Voice ja-JP:</label>
          </div>
          <div className="mr-2">
            <Form.Control
              as="select"
              {...register('jaJpVoiceId', { required: true })}
              onChange={(e) => {
                const filtered = voices['ja-JP'].filter((item) => item.voiceId === e.target.value);
                setJaJpVoice((prevState) => {
                  return {
                    ...prevState,
                    voiceId: e.target.value,
                    engines: filtered[0].engines,
                    domains: filtered[0].domains,
                    gender: filtered[0].gender,
                    provider: filtered[0].provider,
                  };
                });
              }}
            >
              {voices['ja-JP']?.map((item) => (
                <option key={item.voiceId} value={item.voiceId}>
                  {item.provider}: {item.voiceId} - {item.gender}
                </option>
              ))}
            </Form.Control>
            {errors.jaJpVoiceId?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div className="mr-2">
            <Form.Control as="select" {...register('jaJpEngine', { required: true })}>
              {jaJpVoice?.engines?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.jaJpEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'block' }}>
            <Form.Control as="select" {...register('jaJpDomain', { required: true })}>
              {jaJpVoice?.domains?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="empty">empty</option>
            </Form.Control>
            {errors.jaJpEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'none' }}>
            <Form.Control as="select" {...register('jaJpProvider', { required: true })}>
              <option key={jaJpVoice.provider} value={jaJpVoice.provider}>
                {jaJpVoice.provider}
              </option>
            </Form.Control>
            {errors.jaJpProvider?.type === 'required' && (
              <div className="text-red-500 text-left">This field is required</div>
            )}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Voice zh-CN:</label>
          </div>
          <div className="mr-2">
            <Form.Control
              as="select"
              {...register('zhCnVoiceId', { required: true })}
              onChange={(e) => {
                const filtered = voices['zh-CN'].filter((item) => item.voiceId === e.target.value);
                setZhCnVoice((prevState) => {
                  return {
                    ...prevState,
                    voiceId: e.target.value,
                    engines: filtered[0].engines,
                    domains: filtered[0].domains,
                    gender: filtered[0].gender,
                    provider: filtered[0].provider,
                  };
                });
              }}
            >
              {voices['zh-CN']?.map((item) => (
                <option key={item.voiceId} value={item.voiceId}>
                  {item.provider}: {item.voiceId} - {item.gender}
                </option>
              ))}
            </Form.Control>
            {errors.zhCnVoiceId?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div className="mr-2">
            <Form.Control as="select" {...register('zhCnEngine', { required: true })}>
              {zhCnVoice?.engines?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.zhCnEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'block' }}>
            <Form.Control as="select" {...register('zhCnDomain', { required: true })}>
              {zhCnVoice?.domains?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="empty">empty</option>
            </Form.Control>
            {errors.zhCnEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'none' }}>
            <Form.Control as="select" {...register('zhCnProvider', { required: true })}>
              <option key={zhCnVoice.provider} value={zhCnVoice.provider}>
                {zhCnVoice.provider}
              </option>
            </Form.Control>
            {errors.zhCnProvider?.type === 'required' && (
              <div className="text-red-500 text-left">This field is required</div>
            )}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Voice es-ES:</label>
          </div>
          <div className="mr-2">
            <Form.Control
              as="select"
              {...register('esEsVoiceId', { required: true })}
              onChange={(e) => {
                const filtered = voices['es-ES'].filter((item) => item.voiceId === e.target.value);
                setEsEsVoice((prevState) => {
                  return {
                    ...prevState,
                    voiceId: e.target.value,
                    engines: filtered[0].engines,
                    domains: filtered[0].domains,
                    gender: filtered[0].gender,
                    provider: filtered[0].provider,
                  };
                });
              }}
            >
              {voices['es-ES']?.map((item) => (
                <option key={item.voiceId} value={item.voiceId}>
                  {item.provider}: {item.voiceId} - {item.gender}
                </option>
              ))}
            </Form.Control>
            {errors.esEsVoiceId?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div className="mr-2">
            <Form.Control as="select" {...register('esEsEngine', { required: true })}>
              {esEsVoice?.engines?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.esEsEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'block' }}>
            <Form.Control as="select" {...register('esEsDomain', { required: true })}>
              {esEsVoice?.domains?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="empty">empty</option>
            </Form.Control>
            {errors.esEsEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'none' }}>
            <Form.Control as="select" {...register('esEsProvider', { required: true })}>
              <option key={esEsVoice.provider} value={esEsVoice.provider}>
                {esEsVoice.provider}
              </option>
            </Form.Control>
            {errors.esEsProvider?.type === 'required' && (
              <div className="text-red-500 text-left">This field is required</div>
            )}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Voice es-MX:</label>
          </div>
          <div className="mr-2">
            <Form.Control
              as="select"
              {...register('esMxVoiceId', { required: true })}
              onChange={(e) => {
                const filtered = voices['es-MX'].filter((item) => item.voiceId === e.target.value);
                setEsMxVoice((prevState) => {
                  return {
                    ...prevState,
                    voiceId: e.target.value,
                    engines: filtered[0].engines,
                    domains: filtered[0].domains,
                    gender: filtered[0].gender,
                    provider: filtered[0].provider,
                  };
                });
              }}
            >
              {voices['es-MX']?.map((item) => (
                <option key={item.voiceId} value={item.voiceId}>
                  {item.provider}: {item.voiceId} - {item.gender}
                </option>
              ))}
            </Form.Control>
            {errors.esMxVoiceId?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div className="mr-2">
            <Form.Control as="select" {...register('esMxEngine', { required: true })}>
              {esMxVoice?.engines?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.esMxEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'block' }}>
            <Form.Control as="select" {...register('esMxDomain', { required: true })}>
              {esMxVoice?.domains?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="empty">empty</option>
            </Form.Control>
            {errors.esMxEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'none' }}>
            <Form.Control as="select" {...register('esMxProvider', { required: true })}>
              <option key={esMxVoice.provider} value={esMxVoice.provider}>
                {esMxVoice.provider}
              </option>
            </Form.Control>
            {errors.esMxProvider?.type === 'required' && (
              <div className="text-red-500 text-left">This field is required</div>
            )}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Voice es-US:</label>
          </div>
          <div className="mr-2">
            <Form.Control
              as="select"
              {...register('esUsVoiceId', { required: true })}
              onChange={(e) => {
                const filtered = voices['es-US'].filter((item) => item.voiceId === e.target.value);
                setEsUsVoice((prevState) => {
                  return {
                    ...prevState,
                    voiceId: e.target.value,
                    engines: filtered[0].engines,
                    domains: filtered[0].domains,
                    gender: filtered[0].gender,
                    provider: filtered[0].provider,
                  };
                });
              }}
            >
              {voices['es-US']?.map((item) => (
                <option key={item.voiceId} value={item.voiceId}>
                  {item.provider}: {item.voiceId} - {item.gender}
                </option>
              ))}
            </Form.Control>
            {errors.esUsVoiceId?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div className="mr-2">
            <Form.Control as="select" {...register('esUsEngine', { required: true })}>
              {esUsVoice?.engines?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.esUsEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'block' }}>
            <Form.Control as="select" {...register('esUsDomain', { required: true })}>
              {esUsVoice?.domains?.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              <option value="empty">empty</option>
            </Form.Control>
            {errors.esUsEngine?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>

          <div style={{ display: 'none' }}>
            <Form.Control as="select" {...register('esUsProvider', { required: true })}>
              <option key={esUsVoice.provider} value={esUsVoice.provider}>
                {esUsVoice.provider}
              </option>
            </Form.Control>
            {errors.esUsProvider?.type === 'required' && (
              <div className="text-red-500 text-left">This field is required</div>
            )}
          </div>
        </div>
        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">prosodyRate:</label>
          </div>
          <div>
            <Form.Control as="select" {...register('prosodyRate', { required: true })}>
              {prosodyRateValues.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Control>
            {errors.prosodyRate?.type === 'required' && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">
              Support for retrieval practice:
            </label>
          </div>
          <div className="form-checkbox">
            <Form.Check type="checkbox" {...register('retrieval')} />
          </div>
        </div>

        <div className="flex items-center mb-6">
          <div className="width-fix">
            <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">Groups:</label>
          </div>
          <div style={{ width: '250px' }}>
            <Select
              ref={selectRef}
              isMulti
              onChange={onSelectChange}
              options={groupsSelect()}
              placeholder=""
              closeMenuOnSelect={false}
              defaultValue={groupsValues}
            />
            {groupsValues?.length === 0 && <div className="text-red-500 text-left">This field is required</div>}
          </div>
        </div>

        <div className="flex items-center">
          <div className="">
            <Button variant="success" disabled={submitting} type="submit">
              {submitting && (
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: '4px' }}
                />
              )}
              {id === 'add' ? 'Add Podcast' : 'Update Podcast'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    error: state.podcasts.error,
    message: state.podcasts.message,
    loading: state.podcasts.loading,
  };
};

const dispatchToProps: DispatchProps = {
  startAddingPodcast,
  addedPodcast,
  startUpdatingPodcast,
  updatedPodcast,
};

export default connect(mapStateToProps, dispatchToProps)(withRouter(PodcastForm));
